export interface IContainerPlatform {
    architecture?: string;
    os?: string;
}

export enum CONTAINER_STATE {
    CREATED = 'created',
    RESTARTING = 'restarting',
    RUNNING = 'running',
    REMOVING = 'removing',
    PAUSED = 'paused',
    EXITED = 'exited',
    DEAD = 'dead',
}

export const CONTAINER_STATE_LABEL: Record<CONTAINER_STATE, string> = {
    [CONTAINER_STATE.CREATED]: 'Created',
    [CONTAINER_STATE.RESTARTING]: 'Restarting',
    [CONTAINER_STATE.RUNNING]: 'Running',
    [CONTAINER_STATE.REMOVING]: 'Removing',
    [CONTAINER_STATE.PAUSED]: 'Paused',
    [CONTAINER_STATE.EXITED]: 'Exited',
    [CONTAINER_STATE.DEAD]: 'Dead',
};

export const STOPPED_CONTAINER_STATES: readonly CONTAINER_STATE[] = [
    CONTAINER_STATE.EXITED,
    CONTAINER_STATE.DEAD,
];

export const CONTAINER_STATE_FILTERS = Object.values(CONTAINER_STATE).map((state) => ({
    text: CONTAINER_STATE_LABEL[state],
    value: state,
}));
const AWS_DEFAULT_REGEX_PATTERN = /^[^/]+\.dkr\.ecr\.[^/]+\.amazonaws\.com\//;
export class ContainerDto {
    constructor(private readonly _object: unknown) {
    }

    get id(): string {
        return this._object['id'] || '';
    }

    get names(): string {
        return this._object['names'] || '';
    }

    get image(): string {
        return this._object['image'] || '';
    }

    get displayImageName(): string {
        return this.image.replace(AWS_DEFAULT_REGEX_PATTERN, '');
    }

    get command(): string {
        return this._object['command'] || '';
    }

    get labels(): string {
        return this._object['labels'] || '';
    }

    get localVolumes(): string {
        return this._object['localVolumes'] || '';
    }

    get mounts(): string {
        return this._object['mounts'] || '';
    }

    get networks(): string {
        return this._object['networks'] || '';
    }

    get platform(): IContainerPlatform | null {
        return this._object['platform'] || null;
    }

    get ports(): string {
        return this._object['ports'] || '';
    }

    get runningFor(): string {
        return this._object['runningFor'] || '';
    }

    get size(): string {
        return this._object['size'] || '';
    }

    get state(): string {
        return this._object['state'] || '';
    }

    get status(): string {
        return this._object['status'] || '';
    }

    get startedAt(): Date {
        return this._object['startedAt'] ? new Date(this._object['startedAt']) : null;
    }

    get finishedAt(): Date {
        return this._object['finishedAt'] ? new Date(this._object['finishedAt']) : null;
    }

    get isRunning(): boolean {
        return this.state === CONTAINER_STATE.RUNNING;
    }

    get isStopped(): boolean {
        return STOPPED_CONTAINER_STATES.includes(this.state as CONTAINER_STATE);
    }

    get stateLabel(): string {
        return CONTAINER_STATE_LABEL[this.state as CONTAINER_STATE] || this.state;
    }

    public clone(overrides: Partial<Record<string, unknown>>): ContainerDto {
        return new ContainerDto({ ...(this._object as object), ...overrides });
    }
}
